// 스테이지 지도 가장자리(S2, three 없음): 1m 판정 격자 → 부드러운 거리장 → 마칭 스퀘어 등고선.
// 판정은 여전히 1m 격자다. 여기서는 보이는 가장자리만 만든다: 바닥 윗면 다각형, 가장자리 사슬(절벽 단면·트림 배치용), 얕은 가장자리 거리 질감.
// 거리장 F > 0 이 바닥. 격자 경계에서 바깥으로 BIAS만큼 부풀리고(영웅이 서 있는 칸은 늘 바닥 위), 경계 근처에만 잡음을 섞어
// 사각형 격자 모서리를 깨진 돌 테두리로 바꾼다. 같은 시드면 같은 모양이다.
import { Cell, type EdgeStyle, type MapDef, type MapGrid } from '../../shared/levels';
import { hash2, valueNoise } from './rng';

/** 거리장 격자 간격(m) */
export const FIELD_STEP = 0.5;
/** 격자 경계에서 바깥으로 부풀리는 양(m) */
const BIAS = 0.16;
/** 경계 잡음 진폭(m) */
const WOBBLE = 0.32;

export interface StageField {
  /** 격자 점 (i, j)의 월드 좌표 = (ox + i·h, oz + j·h) */
  readonly ox: number;
  readonly oz: number;
  readonly h: number;
  readonly nx: number;
  readonly nz: number;
  /** > 0 이 바닥 */
  readonly f: Float32Array;
}

/** 지도 판정 격자를 둘러싼 거리장(여백 margin m) */
export function buildField(grid: MapGrid, seed: number, margin = 6): StageField {
  const h = FIELD_STEP;
  const ox = grid.originX - margin, oz = grid.originZ - margin;
  const nx = Math.ceil((grid.width + margin * 2) / h) + 1, nz = Math.ceil((grid.height + margin * 2) / h) + 1;
  const occ = (cx: number, cz: number): number => {
    if (cx < grid.originX || cz < grid.originZ || cx >= grid.originX + grid.width || cz >= grid.originZ + grid.height) return 0;
    return grid.cells[(cz - grid.originZ) * grid.width + (cx - grid.originX)] !== Cell.Void ? 1 : 0;
  };
  const f = new Float32Array(nx * nz);
  const W = 1.5; // 흐림 창 한 변(m)
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const x = ox + i * h, z = oz + j * h;
      // 창 [x−W/2, x+W/2] × [z−W/2, z+W/2]와 겹치는 칸의 점유 비율
      let sum = 0;
      const x0 = Math.floor(x - W / 2), x1 = Math.ceil(x + W / 2), z0 = Math.floor(z - W / 2), z1 = Math.ceil(z + W / 2);
      for (let cz = z0; cz < z1; cz++) {
        const oz2 = Math.max(0, Math.min(cz + 1, z + W / 2) - Math.max(cz, z - W / 2));
        if (oz2 <= 0) continue;
        for (let cx = x0; cx < x1; cx++) {
          const ox2 = Math.max(0, Math.min(cx + 1, x + W / 2) - Math.max(cx, x - W / 2));
          if (ox2 > 0 && occ(cx, cz)) sum += ox2 * oz2;
        }
      }
      const g = sum / (W * W); // 0..1, 직선 경계 위에서 0.5
      const d = (g - 0.5) * W; // 경계까지 대략의 부호 있는 거리(m)
      const near = Math.max(0, 1 - Math.abs(d) / 0.9);
      const n = valueNoise(x * 0.9, z * 0.9, seed) * 0.6 + valueNoise(x * 2.3, z * 2.3, seed + 17) * 0.4 - 0.5;
      f[j * nx + i] = d + BIAS + n * 2 * WOBBLE * near;
    }
  }
  return { ox, oz, h, nx, nz, f };
}

export interface ChainPoint { readonly x: number; readonly z: number }
/** 가장자리 사슬: 바깥(빈칸)이 진행 방향의 오른쪽이다. 닫힌 고리면 첫 점 = 마지막 점이 아니라 closed 표시만 한다 */
export interface Chain { readonly pts: ChainPoint[]; readonly closed: boolean }

export interface Contour {
  /** 바닥 윗면 삼각형(x, z 순서쌍 6개 = 한 삼각형, 위를 향하도록 감김) — 깊은 안쪽 칸은 제외(interior가 따로 채운다) */
  readonly band: number[];
  /** 깊은 안쪽 칸 집합 사각형(칸 좌표, 1m 격자). 윗면 사각형 한 장씩으로 그린다 */
  readonly interior: { x0: number; z0: number; x1: number; z1: number }[];
  readonly chains: Chain[];
}

/** 격자 칸 (cx, cz)가 깊은 안쪽(자기와 8이웃이 모두 바닥, 거리장 흐림 창 안이 전부 바닥)인지 */
function deepInside(grid: MapGrid, cx: number, cz: number): boolean {
  for (let dz = -2; dz <= 2; dz++) for (let dx = -2; dx <= 2; dx++) {
    const x = cx + dx, z = cz + dz;
    if (x < grid.originX || z < grid.originZ || x >= grid.originX + grid.width || z >= grid.originZ + grid.height) return false;
    if (grid.cells[(z - grid.originZ) * grid.width + (x - grid.originX)] === Cell.Void) return false;
  }
  return true;
}

/** 칸 집합을 겹침 없는 사각형으로(shapes.rectsOfCells의 단순판, 한 행 구간 + 세로 합치기) */
function mergeCells(cells: Set<string>): { x0: number; z0: number; x1: number; z1: number }[] {
  const rows = new Map<number, number[]>();
  for (const k of cells) { const [x, z] = k.split(',').map(Number); let a = rows.get(z); if (!a) rows.set(z, (a = [])); a.push(x); }
  const zs = [...rows.keys()].sort((a, b) => a - b);
  let open = new Map<string, { x0: number; z0: number; x1: number; z1: number }>();
  const out: { x0: number; z0: number; x1: number; z1: number }[] = [];
  let prev = Number.NaN;
  for (const z of zs) {
    const xs = rows.get(z)!.sort((a, b) => a - b);
    const next = new Map<string, { x0: number; z0: number; x1: number; z1: number }>();
    for (let i = 0; i < xs.length;) {
      let j = i;
      while (j + 1 < xs.length && xs[j + 1] === xs[j] + 1) j++;
      const key = xs[i] + ':' + xs[j];
      const cur = prev === z - 1 ? open.get(key) : undefined;
      if (cur) { open.delete(key); next.set(key, { ...cur, z1: z + 1 }); } else next.set(key, { x0: xs[i], z0: z, x1: xs[j] + 1, z1: z + 1 });
      i = j + 1;
    }
    for (const r of open.values()) out.push(r);
    open = next; prev = z;
  }
  for (const r of open.values()) out.push(r);
  return out;
}

/** 등고선(F = 0) 추출 */
export function contourOf(field: StageField, grid: MapGrid, skip?: (x: number, z: number) => boolean): Contour {
  const { ox, oz, h, nx, nz, f } = field;
  const band: number[] = [];
  const segs: { a: string; b: string; ax: number; az: number; bx: number; bz: number }[] = [];
  const val = (i: number, j: number): number => f[j * nx + i];
  const fAt = (x: number, z: number): number => {
    const fx = Math.min(nx - 1.001, Math.max(0, (x - ox) / h)), fz = Math.min(nz - 1.001, Math.max(0, (z - oz) / h));
    const i = Math.floor(fx), j = Math.floor(fz), u = fx - i, v = fz - j;
    return (val(i, j) * (1 - u) + val(i + 1, j) * u) * (1 - v) + (val(i, j + 1) * (1 - u) + val(i + 1, j + 1) * u) * v;
  };
  // 깊은 안쪽 1m 칸
  const interiorCells = new Set<string>();
  for (let cz = grid.originZ; cz < grid.originZ + grid.height; cz++) for (let cx = grid.originX; cx < grid.originX + grid.width; cx++) {
    if (grid.cells[(cz - grid.originZ) * grid.width + (cx - grid.originX)] !== Cell.Void && deepInside(grid, cx, cz) && !(skip && skip(cx + 0.5, cz + 0.5))) interiorCells.add(cx + ',' + cz);
  }
  const isInterior = (cx: number, cz: number): boolean => interiorCells.has(cx + ',' + cz);
  for (let j = 0; j < nz - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      // 이 격자 칸(0.5m)이 속한 1m 칸이 깊은 안쪽이면 interior가 채운다
      const mx = ox + (i + 0.5) * h, mz = oz + (j + 0.5) * h;
      if (isInterior(Math.floor(mx), Math.floor(mz)) || (skip && skip(mx, mz))) continue;
      const cs: [number, number][] = [[i, j], [i + 1, j], [i + 1, j + 1], [i, j + 1]];
      const vs = cs.map(([a, b]) => val(a, b));
      const inside = vs.map((v) => v > 0);
      if (!inside.some(Boolean)) continue;
      // 안쪽 다각형(꼭짓점 + 교차점)
      const poly: number[] = [];
      const cross: { x: number; z: number; id: string }[] = [];
      for (let k = 0; k < 4; k++) {
        const k2 = (k + 1) & 3;
        const [ai, aj] = cs[k], [bi, bj] = cs[k2];
        if (inside[k]) poly.push(ox + ai * h, oz + aj * h);
        if (inside[k] !== inside[k2]) {
          const t = vs[k] / (vs[k] - vs[k2]);
          const x = ox + (ai + (bi - ai) * t) * h, z = oz + (aj + (bj - aj) * t) * h;
          poly.push(x, z);
          // 격자 변 번호(같은 변을 이웃 칸이 같은 번호로 부른다)
          const id = ai === bi ? 'v:' + ai + ':' + Math.min(aj, bj) : 'h:' + Math.min(ai, bi) + ':' + aj;
          cross.push({ x, z, id });
        }
      }
      const n = poly.length / 2;
      for (let k = 1; k < n - 1; k++) {
        const a = [poly[0], poly[1]], b = [poly[k * 2], poly[k * 2 + 1]], c = [poly[(k + 1) * 2], poly[(k + 1) * 2 + 1]];
        // 위(+y)를 향하게: (b−a)×(c−a)의 y 성분 = (bz−az)(cx−ax) − (bx−ax)(cz−az)  → 양수여야 한다
        const cy = (b[1] - a[1]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[1] - a[1]);
        if (Math.abs(cy) < 1e-9) continue;
        if (cy > 0) band.push(a[0], a[1], b[0], b[1], c[0], c[1]); else band.push(a[0], a[1], c[0], c[1], b[0], b[1]);
      }
      // 등고선 선분(교차점 2개 또는 안장 4개)
      for (let s = 0; s + 1 < cross.length; s += 2) {
        let p = cross[s], q = cross[s + 1];
        // 오른쪽 = 바깥(빈칸): 진행 방향 d의 오른쪽 법선 = (dz, −dx)
        const dx = q.x - p.x, dz = q.z - p.z, len = Math.hypot(dx, dz);
        if (len < 1e-6) continue;
        const mxm = (p.x + q.x) / 2, mzm = (p.z + q.z) / 2;
        if (fAt(mxm + (dz / len) * 0.25, mzm - (dx / len) * 0.25) > 0) { const t = p; p = q; q = t; }
        segs.push({ a: p.id, b: q.id, ax: p.x, az: p.z, bx: q.x, bz: q.z });
      }
    }
  }
  // 선분 → 사슬(시작 변 번호로 찾는다)
  const byStart = new Map<string, number[]>();
  segs.forEach((s, i) => { let a = byStart.get(s.a); if (!a) byStart.set(s.a, (a = [])); a.push(i); });
  const used = new Uint8Array(segs.length);
  const hasPrev = new Set<string>();
  for (const s of segs) hasPrev.add(s.b);
  const chains: Chain[] = [];
  const walk = (start: number): Chain => {
    const pts: ChainPoint[] = [];
    let cur = start, closed = false;
    const first = segs[start].a;
    while (cur >= 0 && !used[cur]) {
      used[cur] = 1;
      const s = segs[cur];
      pts.push({ x: s.ax, z: s.az });
      const nexts = byStart.get(s.b);
      let nxt = -1;
      if (nexts) for (const c of nexts) if (!used[c]) { nxt = c; break; }
      if (nxt < 0) { pts.push({ x: s.bx, z: s.bz }); if (s.b === first) closed = true; }
      cur = nxt;
    }
    return { pts, closed };
  };
  // 열린 사슬(앞이 없는 선분)부터, 그다음 닫힌 고리
  segs.forEach((s, i) => { if (!used[i] && !hasPrev.has(s.a)) chains.push(walk(i)); });
  segs.forEach((s, i) => { if (!used[i]) { const c = walk(i); chains.push({ pts: c.pts, closed: true }); } });
  return { band, interior: mergeCells(interiorCells), chains };
}

/** 가장자리 표본 하나(트림·덩굴·풀 배치용) */
export interface EdgeSample {
  readonly x: number;
  readonly z: number;
  /** 진행 방향(단위) */
  readonly tx: number;
  readonly tz: number;
  /** 바깥(빈칸 쪽) 법선(단위) */
  readonly nx: number;
  readonly nz: number;
  /** 이 표본에서 꺾이는 각(rad, 양수 = 바깥으로 볼록한 모서리 = 바닥 입장에서 바깥 모서리, 음수 = 안쪽 모서리) */
  readonly turn: number;
}

/** 사슬을 spacing(m) 간격으로 표본 추출(닫힌 고리는 한 바퀴) */
export function resample(chain: Chain, spacing: number): EdgeSample[] {
  const pts = chain.pts;
  if (pts.length < 2) return [];
  const len: number[] = [0];
  for (let i = 1; i < pts.length; i++) len.push(len[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].z - pts[i - 1].z));
  const total = len[len.length - 1] + (chain.closed ? Math.hypot(pts[0].x - pts[pts.length - 1].x, pts[0].z - pts[pts.length - 1].z) : 0);
  if (total < spacing * 0.5) return [];
  const count = Math.max(1, Math.round(total / spacing));
  const step = total / count;
  const at = (d: number): { x: number; z: number; tx: number; tz: number } => {
    let dd = chain.closed ? ((d % total) + total) % total : Math.max(0, Math.min(total, d));
    let i = 1;
    const m = len.length;
    while (i < m && len[i] < dd) i++;
    let a: ChainPoint, b: ChainPoint, t: number;
    if (i >= m) { a = pts[m - 1]; b = pts[0]; t = (dd - len[m - 1]) / Math.max(1e-6, total - len[m - 1]); if (!chain.closed) { a = pts[m - 2]; b = pts[m - 1]; t = 1; } }
    else { a = pts[i - 1]; b = pts[i]; t = (dd - len[i - 1]) / Math.max(1e-6, len[i] - len[i - 1]); }
    const dx = b.x - a.x, dz = b.z - a.z, l = Math.hypot(dx, dz) || 1;
    return { x: a.x + dx * t, z: a.z + dz * t, tx: dx / l, tz: dz / l };
  };
  const out: EdgeSample[] = [];
  const end = chain.closed ? count : count + 1;
  for (let k = 0; k < end; k++) {
    const d = k * step;
    const p = at(d), pa = at(d - step * 0.5), pb = at(d + step * 0.5);
    // 진행 방향 = 앞뒤 구간 평균
    let tx = pa.tx + pb.tx, tz = pa.tz + pb.tz;
    const tl = Math.hypot(tx, tz) || 1; tx /= tl; tz /= tl;
    // 꺾임: pa → pb 방향 변화. 오른쪽이 바깥이므로 오른쪽으로 꺾이면(cross > 0 이 아님…) 바깥 모서리
    const cross = pa.tx * pb.tz - pa.tz * pb.tx;
    const dot = pa.tx * pb.tx + pa.tz * pb.tz;
    const turn = -Math.atan2(cross, dot);
    out.push({ x: p.x, z: p.z, tx, tz, nx: tz, nz: -tx, turn });
  }
  return out;
}

/** 가장자리 모양 찾기: 구역(앞에서부터 처음 맞는 것) → 지도 기본 → cliff */
export function edgeStyleAt(map: MapDef, x: number, z: number): EdgeStyle {
  for (const zone of map.edgeZones ?? []) if (x >= zone.rect.x0 && x <= zone.rect.x1 && z >= zone.rect.z0 && z <= zone.rect.z1) return zone.style;
  return map.edgeStyle ?? 'cliff';
}
/** 점이 수면 안인지 */
export function inWater(map: MapDef, x: number, z: number): boolean {
  for (const w of map.water ?? []) if (x >= w.x0 && x <= w.x1 && z >= w.z0 && z <= w.z1) return true;
  return false;
}

/**
 * 바닥 가장자리 거리 질감(칸마다 0..255): 빈칸에서 안쪽으로 maxIn칸까지 1 → 0으로 줄어든다(바닥 가장자리 이끼·풀·젖음).
 * 질감 크기 = 격자 크기. 빈칸은 0.
 */
export function edgeProximity(grid: MapGrid, maxIn = 3): Uint8Array {
  const { width: W, height: H } = grid;
  const d = new Float32Array(W * H).fill(99);
  const q: number[] = [];
  for (let i = 0; i < W * H; i++) if (grid.cells[i] === Cell.Void) { d[i] = 0; q.push(i); }
  for (let head = 0; head < q.length; head++) {
    const c = q[head], x = c % W, z = (c / W) | 0;
    for (let k = 0; k < 4; k++) {
      const nx = x + (k === 0 ? 1 : k === 1 ? -1 : 0), nz = z + (k === 2 ? 1 : k === 3 ? -1 : 0);
      if (nx < 0 || nz < 0 || nx >= W || nz >= H) continue;
      const nc = nz * W + nx;
      if (d[nc] > d[c] + 1) { d[nc] = d[c] + 1; q.push(nc); }
    }
  }
  const out = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) out[i] = grid.cells[i] === Cell.Void ? 0 : Math.round(Math.max(0, 1 - (d[i] - 0.5) / maxIn) * 255);
  return out;
}

/** 바닥에서 바깥(빈칸)으로의 거리(칸마다 0..255 = 0..maxOut m, 바닥은 0): 수면 얕은 곳·물거품 */
export function shoreDistance(grid: MapGrid, maxOut = 8): Uint8Array {
  const { width: W, height: H } = grid;
  const d = new Float32Array(W * H).fill(99);
  const q: number[] = [];
  for (let i = 0; i < W * H; i++) if (grid.cells[i] !== Cell.Void) { d[i] = 0; q.push(i); }
  for (let head = 0; head < q.length; head++) {
    const c = q[head], x = c % W, z = (c / W) | 0;
    for (let k = 0; k < 4; k++) {
      const nx = x + (k === 0 ? 1 : k === 1 ? -1 : 0), nz = z + (k === 2 ? 1 : k === 3 ? -1 : 0);
      if (nx < 0 || nz < 0 || nx >= W || nz >= H) continue;
      const nc = nz * W + nx;
      if (d[nc] > d[c] + 1) { d[nc] = d[c] + 1; q.push(nc); }
    }
  }
  const out = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) out[i] = Math.round(Math.min(1, d[i] / maxOut) * 255);
  return out;
}

export { hash2 };
